import * as React from "react"
import { cn } from "@/lib/utils"

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  collapsed?: boolean
}

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  ({ className, collapsed = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex h-full flex-col border-r bg-card",
          collapsed ? "w-16" : "w-[232px]",
          "transition-all duration-300",
          className
        )}
        {...props}
      />
    )
  }
)
Sidebar.displayName = "Sidebar"

const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex h-16 items-center border-b px-6", className)}
    {...props}
  />
))
SidebarHeader.displayName = "SidebarHeader"

const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex-1 overflow-auto py-4", className)}
    {...props}
  />
))
SidebarContent.displayName = "SidebarContent"

const SidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("px-3 py-2", className)} {...props} />
))
SidebarGroup.displayName = "SidebarGroup"

const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider",
      className
    )}
    {...props}
  />
))
SidebarGroupLabel.displayName = "SidebarGroupLabel"

const SidebarGroupContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("space-y-1", className)} {...props} />
))
SidebarGroupContent.displayName = "SidebarGroupContent"

interface SidebarMenuItemProps extends React.HTMLAttributes<HTMLButtonElement> {
  active?: boolean
  icon?: React.ReactNode
  badge?: React.ReactNode
}

const SidebarMenuItem = React.forwardRef<HTMLButtonElement, SidebarMenuItemProps>(
  ({ className, active, icon, badge, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          "relative flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all duration-200",
          active
            ? [
                "bg-secondary text-foreground",
                "before:absolute before:left-0 before:top-1/2 before:h-5 before:w-[3px]",
                "before:-translate-y-1/2 before:rounded-r-full before:bg-primary",
                "before:[box-shadow:0_0_6px_#b5e84a]",
              ].join(" ")
            : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground",
          !children && !badge && "justify-center",
          className
        )}
        {...props}
      >
        {icon && (
          <span className={cn(
            "flex h-5 w-5 items-center justify-center shrink-0 transition-colors",
            active ? "text-foreground" : "text-muted-foreground"
          )}>
            {icon}
          </span>
        )}
        {children && <span className="flex-1 text-left">{children}</span>}
        {badge && <span className="ml-auto shrink-0">{badge}</span>}
      </button>
    )
  }
)
SidebarMenuItem.displayName = "SidebarMenuItem"

const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("border-t px-3 py-4 shrink-0", className)}
    {...props}
  />
))
SidebarFooter.displayName = "SidebarFooter"

export {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenuItem,
  SidebarFooter,
}
